import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ReactFlow, Background, Controls, Handle, Position } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { ArrowLeft, Copy, Flag, MessageSquare, CircleHelp, Tag, PhoneForwarded, PhoneOff, Play, Plus,
         Save, Upload, Workflow, PhoneCall, X, Check, AlertTriangle, Square } from 'lucide-react';
import api from '../api';

// Flujos de voz: llamadas sin IA. Grabación o voz generada -> pregunta -> la persona responde por
// voz o tecla -> el flujo sigue por la opción que eligió. El motor que llama vive en /opt/voxcore-ivr.

const inp = { width: '100%', padding: '9px 12px', background: 'var(--bg)', border: '1px solid var(--border-2)', borderRadius: 9, color: 'var(--text)', fontSize: 13, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' };
const btn = { padding: '9px 16px', background: 'linear-gradient(135deg,var(--accent-solid),var(--accent-2))', border: 'none', color: '#fff', borderRadius: 9, fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 7 };
const btnSec = { padding: '7px 12px', background: 'transparent', border: '1px solid var(--border-2)', color: 'var(--text-2)', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 };
const etiqueta = { fontSize: 11, color: 'var(--text-3)', display: 'block', marginBottom: 5, fontWeight: 600 };

const TIPOS = {
  mensaje:    { nombre: 'Mensaje',    icono: MessageSquare,  color: 'var(--info)',    ayuda: 'Dice algo y sigue' },
  pregunta:   { nombre: 'Pregunta',   icono: CircleHelp,     color: 'var(--accent)',  ayuda: 'Pregunta y escucha voz o tecla' },
  resultado:  { nombre: 'Resultado',  icono: Tag,            color: 'var(--success)', ayuda: 'Tipifica la llamada' },
  transferir: { nombre: 'Transferir', icono: PhoneForwarded, color: 'var(--teal)',    ayuda: 'Pasa a un asesor' },
  colgar:     { nombre: 'Colgar',     icono: PhoneOff,       color: 'var(--danger)',  ayuda: 'Despedida y fin' },
};
const VARIABLES = ['primer_nombre', 'nombre', 'monto_deuda', 'fecha_limite', 'identificacion'];
const esCelular = () => window.matchMedia('(max-width: 760px)').matches;
const NOMBRE_VOZ = v => (v || '').replace('aura-2-', '').replace('-es', '').replace(/^./, c => c.toUpperCase());

function error(e, defecto) { return e?.response?.data?.detail || defecto; }

async function reproducir(promesa, alTerminar) {
  const r = await promesa;
  const url = URL.createObjectURL(r.data);
  const a = new Audio(url);
  a.onended = () => { URL.revokeObjectURL(url); alTerminar && alTerminar(); };
  a.play();
  return a;
}

// ── Página ──────────────────────────────────────────────────────────────────
export default function FlujosVoz() {
  const [estado, setEstado] = useState(null);
  const [flujos, setFlujos] = useState([]);
  const [campanas, setCampanas] = useState([]);
  const [asignadas, setAsignadas] = useState({});
  const [abierto, setAbierto] = useState(null);
  const [msg, setMsg] = useState(null);
  const [nuevo, setNuevo] = useState(null);

  const cargar = useCallback(async () => {
    try {
      const e = await api.get('/flujos-voz/estado');
      setEstado(e.data);
      if (!e.data.activo) return;
      const [f, c, a] = await Promise.all([api.get('/flujos-voz'), api.get('/campaigns/'), api.get('/flujos-voz/campanas')]);
      setFlujos(f.data); setCampanas((c.data || []).filter(x => !x.archivado)); setAsignadas(a.data || {});
    } catch (e) { setEstado({ activo: false }); }
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  const crear = async () => {
    try {
      const r = await api.post('/flujos-voz', { nombre: nuevo || 'Nuevo flujo' });
      setNuevo(null); abrir(r.data.id);
    } catch (e) { setMsg({ ok: false, txt: error(e, 'No se pudo crear el flujo') }); }
  };
  const abrir = async (id) => {
    try { const r = await api.get(`/flujos-voz/${id}`); setAbierto(r.data); }
    catch (e) { setMsg({ ok: false, txt: error(e, 'No se pudo abrir el flujo') }); }
  };
  const duplicar = async (id) => {
    try { await api.post(`/flujos-voz/${id}/duplicar`); setMsg({ ok: true, txt: 'Flujo duplicado' }); cargar(); }
    catch (e) { setMsg({ ok: false, txt: error(e, 'No se pudo duplicar') }); }
  };
  const asignar = async (campanaId, flujoId) => {
    try {
      const r = await api.put(`/flujos-voz/campanas/${campanaId}`, { flujo_id: flujoId || null });
      setAsignadas(r.data);
      setMsg({ ok: true, txt: flujoId ? 'La campaña ahora llama con el flujo de voz' : 'La campaña vuelve a llamar con el agente IA' });
    } catch (e) { setMsg({ ok: false, txt: error(e, 'No se pudo asignar') }); }
  };

  if (!estado) return <div style={{ color: 'var(--text-3)', fontSize: 13 }}>Cargando...</div>;
  if (!estado.activo) return (
    <div style={{ maxWidth: 640 }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>Flujos de voz</h1>
      <p style={{ color: 'var(--text-3)', fontSize: 13, lineHeight: 1.6 }}>
        Llamadas sin IA: un mensaje grabado o con voz generada, una pregunta, y la persona responde por voz o con el teclado.
        Esta función no está activa en tu cuenta. Escríbenos para activarla.
      </p>
    </div>
  );
  if (abierto) return <Editor inicial={abierto} estado={estado} onVolver={() => { setAbierto(null); cargar(); }} />;

  const usoDe = id => campanas.filter(c => asignadas[c.id] === id).map(c => c.nombre_campana);
  return (
    <div style={{ maxWidth: 1040 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 22 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)', marginBottom: 6 }}>Flujos de voz</h1>
          <p style={{ color: 'var(--text-3)', fontSize: 13, margin: 0, maxWidth: 620, lineHeight: 1.55 }}>
            Llamadas sin IA. Arma el recorrido con mensajes y preguntas; la persona responde por voz o con el teclado y cada respuesta lleva a un paso distinto.
          </p>
        </div>
        {nuevo === null
          ? <button style={btn} onClick={() => setNuevo('')}><Plus size={15} />Nuevo flujo</button>
          : <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', width: '100%', maxWidth: 480 }}>
              <input autoFocus style={{ ...inp, width: 240, maxWidth: '100%', flex: '1 1 160px' }} placeholder="Nombre, ej: Recordatorio de pago" value={nuevo}
                onChange={e => setNuevo(e.target.value)} onKeyDown={e => e.key === 'Enter' && crear()} />
              <button style={btn} onClick={crear}><Check size={15} />Crear</button>
              <button style={btnSec} onClick={() => setNuevo(null)}>Cancelar</button>
            </div>}
      </div>

      {msg && <Aviso msg={msg} onCerrar={() => setMsg(null)} />}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(min(300px,100%),1fr))', gap: 14, marginBottom: 32 }}>
        {flujos.filter(f => !f.archivado).map(f => {
          const uso = usoDe(f.id);
          return (
            <div key={f.id} className="ic-card" style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 34, height: 34, borderRadius: 9, background: 'var(--accent-soft)', color: 'var(--accent)', display: 'grid', placeItems: 'center', flexShrink: 0 }}><Workflow size={17} /></div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.nombre}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-3)' }}>{f.pasos} pasos</div>
                </div>
              </div>
              <div>
                {f.errores.length
                  ? <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--warning)', background: 'color-mix(in srgb, var(--warning) 12%, transparent)', padding: '3px 8px', borderRadius: 20 }}>{f.errores.length} pendiente{f.errores.length > 1 ? 's' : ''}</span>
                  : <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--success)', background: 'var(--success-soft)', padding: '3px 8px', borderRadius: 20 }}>Listo para usar</span>}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-2)', minHeight: 18 }}>{uso.length ? `En uso: ${uso.join(', ')}` : 'Sin campañas'}</div>
              <div style={{ display: 'flex', gap: 8, marginTop: 'auto' }}>
                <button style={{ ...btnSec, color: 'var(--text)' }} onClick={() => abrir(f.id)}>Abrir</button>
                <button style={btnSec} onClick={() => duplicar(f.id)}><Copy size={13} />Duplicar</button>
              </div>
            </div>
          );
        })}
        {!flujos.filter(f => !f.archivado).length && <div style={{ color: 'var(--text-3)', fontSize: 13 }}>Aún no tienes flujos. Crea el primero: arranca con un ejemplo de recordatorio de pago que puedes cambiar.</div>}
      </div>

      <h2 style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', marginBottom: 6 }}>¿Cómo llama cada campaña?</h2>
      <p style={{ color: 'var(--text-3)', fontSize: 12, marginTop: 0, marginBottom: 12 }}>Las campañas con flujo de voz marcan igual (barridos, horarios, reintentos), pero sin agente IA. Solo se pueden elegir flujos sin pendientes.</p>
      <div className="ic-card" style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
        {campanas.map((c, i) => (
          <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 18px', borderBottom: i < campanas.length - 1 ? '1px solid var(--border)' : 'none', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 200, fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{c.nombre_campana}</div>
            <select style={{ ...inp, width: 280, maxWidth: '100%' }} value={asignadas[c.id] || ''} onChange={e => asignar(c.id, e.target.value)}>
              <option value="">Agente IA</option>
              {flujos.filter(f => !f.archivado && (!f.errores.length || asignadas[c.id] === f.id)).map(f => <option key={f.id} value={f.id}>Flujo de voz: {f.nombre}</option>)}
            </select>
          </div>
        ))}
        {!campanas.length && <div style={{ padding: 18, color: 'var(--text-3)', fontSize: 13 }}>No hay campañas.</div>}
      </div>
    </div>
  );
}

function Aviso({ msg, onCerrar }) {
  return (
    <div style={{ marginBottom: 16, padding: '10px 14px', borderRadius: 9, fontSize: 13, fontWeight: 600, display: 'flex', gap: 10, alignItems: 'center',
                  background: msg.ok ? 'var(--success-soft)' : 'var(--danger-soft)', color: msg.ok ? 'var(--success)' : 'var(--danger)' }}>
      <span style={{ flex: 1 }}>{msg.txt}</span>
      <button onClick={onCerrar} aria-label="Cerrar" style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer' }}><X size={14} /></button>
    </div>
  );
}

// ── Nodo del lienzo ─────────────────────────────────────────────────────────
const salida = { width: 10, height: 10, background: 'var(--surface)', border: '2px solid var(--text-3)' };

function PasoNodo({ data, selected }) {
  const { nodo, esInicio } = data;
  const t = TIPOS[nodo.tipo] || TIPOS.mensaje;
  const Icono = t.icono;
  const texto = nodo.audio?.modo === 'grabacion' ? `Grabación: ${nodo.audio.nombre || 'audio'}`
    : nodo.tipo === 'resultado' ? `${nodo.status || ''} · ${nodo.resultado || ''}`
    : nodo.tipo === 'transferir' ? (nodo.numero || 'Sin número') : (nodo.audio?.texto || '');
  const fila = { position: 'relative', fontSize: 11, color: 'var(--text-2)', padding: '5px 12px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', gap: 8 };
  return (
    <div style={{ width: 240, background: 'var(--surface)', borderRadius: 12, fontFamily: 'Inter,sans-serif',
                  border: `1.5px solid ${selected ? t.color : 'var(--border-2)'}`,
                  boxShadow: selected ? `0 0 0 3px color-mix(in srgb, ${t.color} 22%, transparent)` : 'var(--shadow-sm)' }}>
      <Handle type="target" position={Position.Left} style={{ ...salida, borderColor: t.color }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px 6px' }}>
        <div style={{ width: 24, height: 24, borderRadius: 7, background: `color-mix(in srgb, ${t.color} 16%, transparent)`, color: t.color, display: 'grid', placeItems: 'center', flexShrink: 0 }}><Icono size={13} /></div>
        <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{nodo.titulo || t.nombre}</div>
        {esInicio && <span style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: '0.04em', color: 'var(--accent)', background: 'var(--accent-soft)', padding: '2px 6px', borderRadius: 10, textTransform: 'uppercase' }}>Inicio</span>}
      </div>
      <div style={{ padding: '0 12px 9px' }}>
        <div style={{ fontSize: 11.5, color: 'var(--text-3)', lineHeight: 1.45, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{texto || '—'}</div>
      </div>
      {nodo.tipo === 'pregunta' && <>
        {(nodo.opciones || []).map(o => (
          <div key={o.id} style={fila}>
            <span style={{ fontWeight: 600, color: 'var(--text)' }}>{o.nombre || 'Opción'}</span>
            <span>{o.tecla ? `tecla ${o.tecla}` : ''}</span>
            <Handle type="source" id={`op:${o.id}`} position={Position.Right} style={{ ...salida, borderColor: t.color }} />
          </div>
        ))}
        <div style={fila}><span>Sin respuesta</span><Handle type="source" id="sin" position={Position.Right} style={salida} /></div>
        <div style={fila}><span>No entendió</span><Handle type="source" id="noent" position={Position.Right} style={salida} /></div>
      </>}
      {['mensaje', 'resultado'].includes(nodo.tipo) &&
        <Handle type="source" id="sig" position={Position.Right} style={{ ...salida, borderColor: t.color }} />}
    </div>
  );
}
const tiposNodo = { paso: PasoNodo };

function campoDeSalida(handle) {
  if (handle === 'sig') return { campo: 'siguiente' };
  if (handle === 'sin') return { campo: 'sin_respuesta' };
  if (handle === 'noent') return { campo: 'no_entendio' };
  if (handle?.startsWith('op:')) return { opcion: handle.slice(3) };
  return null;
}

// ── Editor ──────────────────────────────────────────────────────────────────
function Editor({ inicial, estado, onVolver }) {
  const [flujo, setFlujo] = useState(inicial.flujo);
  const [errores, setErrores] = useState(inicial.errores || []);
  const [sel, setSel] = useState(inicial.flujo.inicio || null);
  const [cambios, setCambios] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [msg, setMsg] = useState(null);
  const [prueba, setPrueba] = useState(null);
  const nodos = useMemo(() => flujo.nodos || {}, [flujo.nodos]);
  // En celular el diagrama completo queda diminuto: se abre legible con el paso de Inicio a la izquierda
  const [vistaInicial] = useState(() => {
    const ini = (inicial.flujo.nodos || {})[inicial.flujo.inicio];
    if (!esCelular() || !ini) return { fitView: true, fitViewOptions: { padding: 0.25, maxZoom: 1 } };
    const z = 0.65;
    return { defaultViewport: { x: 16 - (ini.x || 0) * z, y: 100 - (ini.y || 0) * z, zoom: z } };
  });
  // En celular el panel queda debajo del lienzo: al tocar un paso se baja hasta él
  const panelRef = useRef(null);
  const primeraSel = useRef(true);
  useEffect(() => {
    if (primeraSel.current) { primeraSel.current = false; return; }
    if (sel && esCelular()) panelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [sel]);

  const cambiar = useCallback(f => { setFlujo(f); setCambios(true); }, []);
  const cambiarNodo = useCallback((nid, parche) => {
    setFlujo(f => ({ ...f, nodos: { ...f.nodos, [nid]: { ...f.nodos[nid], ...parche } } }));
    setCambios(true);
  }, []);

  const rfNodes = useMemo(() => Object.entries(nodos).map(([id, n]) => ({
    id, type: 'paso', position: { x: n.x || 0, y: n.y || 0 }, selected: id === sel,
    data: { nodo: n, esInicio: flujo.inicio === id }, deletable: false,
  })), [nodos, sel, flujo.inicio]);

  const rfEdges = useMemo(() => {
    const out = [];
    const arista = (src, handle, dst, txt, color) => dst && nodos[dst] && out.push({
      id: `${src}|${handle}`, source: src, sourceHandle: handle, target: dst, label: txt,
      style: { stroke: color || 'var(--text-3)', strokeWidth: 1.6 },
      labelStyle: { fill: 'var(--text-2)', fontSize: 10, fontWeight: 600 },
      labelBgStyle: { fill: 'var(--surface)' },
    });
    Object.entries(nodos).forEach(([id, n]) => {
      arista(id, 'sig', n.siguiente);
      if (n.tipo === 'pregunta') {
        (n.opciones || []).forEach(o => arista(id, `op:${o.id}`, o.siguiente, o.nombre, 'var(--accent)'));
        arista(id, 'sin', n.sin_respuesta, 'sin respuesta');
        arista(id, 'noent', n.no_entendio, 'no entendió');
      }
    });
    return out;
  }, [nodos]);

  const onNodesChange = useCallback(chs => {
    let mover = null;
    chs.forEach(c => {
      if (c.type === 'position' && c.position) (mover = mover || {})[c.id] = c.position;
      if (c.type === 'select' && c.selected) setSel(c.id);
    });
    if (mover) {
      setFlujo(f => {
        const ns = { ...f.nodos };
        Object.entries(mover).forEach(([id, p]) => { ns[id] = { ...ns[id], x: Math.round(p.x), y: Math.round(p.y) }; });
        return { ...f, nodos: ns };
      });
      setCambios(true);
    }
  }, []);

  const conectar = useCallback((src, handle, dst) => {
    const d = campoDeSalida(handle);
    if (!d || src === dst) return;
    setFlujo(f => {
      const n = { ...f.nodos[src] };
      if (d.campo) n[d.campo] = dst || undefined;
      else n.opciones = (n.opciones || []).map(o => o.id === d.opcion ? { ...o, siguiente: dst || undefined } : o);
      return { ...f, nodos: { ...f.nodos, [src]: n } };
    });
    setCambios(true);
  }, []);
  const onConnect = useCallback(c => conectar(c.source, c.sourceHandle, c.target), [conectar]);
  const onEdgesChange = useCallback(chs => chs.forEach(c => {
    if (c.type === 'remove') { const [src, handle] = c.id.split('|'); conectar(src, handle, null); }
  }), [conectar]);

  const agregar = tipo => {
    const id = `${tipo.slice(0, 3)}_${Math.random().toString(36).slice(2, 7)}`;
    const ys = Object.values(nodos).map(n => n.y || 0);
    const xs = Object.values(nodos).map(n => n.x || 0);
    const base = { tipo, titulo: TIPOS[tipo].nombre, x: (xs.length ? Math.max(...xs) : 0) + 60, y: (ys.length ? Math.min(...ys) : 0) + 40 };
    const extra = {
      mensaje: { audio: { modo: 'texto', texto: '' } },
      pregunta: { audio: { modo: 'texto', texto: '' }, escucha: 'voz_tecla', espera: 6, reintentos: 1,
                  opciones: [{ id: 'si', nombre: 'Sí', palabras: ['si', 'claro'], tecla: '1' }, { id: 'no', nombre: 'No', palabras: ['no'], tecla: '2' }] },
      resultado: { status: 'Finalizada', resultado: '' },
      transferir: { numero: '' },
      colgar: { audio: { modo: 'texto', texto: 'Gracias. Hasta luego.' } },
    }[tipo];
    cambiar({ ...flujo, inicio: flujo.inicio || id, nodos: { ...nodos, [id]: { ...base, ...extra } } });
    setSel(id);
  };

  const quitar = nid => {
    const ns = {};
    Object.entries(nodos).forEach(([id, n]) => {
      if (id === nid) return;
      const m = { ...n };
      ['siguiente', 'sin_respuesta', 'no_entendio'].forEach(k => { if (m[k] === nid) delete m[k]; });
      if (m.opciones) m.opciones = m.opciones.map(o => o.siguiente === nid ? { ...o, siguiente: undefined } : o);
      ns[id] = m;
    });
    cambiar({ ...flujo, inicio: flujo.inicio === nid ? null : flujo.inicio, nodos: ns });
    setSel(null);
  };

  const guardar = async () => {
    setGuardando(true);
    try {
      const r = await api.put(`/flujos-voz/${flujo.id}`, flujo);
      setFlujo(r.data.flujo); setErrores(r.data.errores); setCambios(false);
      setMsg({ ok: true, txt: r.data.errores.length ? 'Guardado. Revisa los pendientes antes de usarlo en una campaña.' : 'Guardado. El flujo está listo para usar.' });
    } catch (e) { setMsg({ ok: false, txt: error(e, 'No se pudo guardar') }); }
    setGuardando(false);
  };

  const volver = () => { if (!cambios || window.confirm('Tienes cambios sin guardar. ¿Salir de todos modos?')) onVolver(); };
  const nodoSel = sel && nodos[sel];

  return (
    <div className="r-editor" style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)', minHeight: 520 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
        <button style={btnSec} onClick={volver}><ArrowLeft size={14} />Flujos</button>
        <input style={{ ...inp, width: 260, flex: '1 1 200px', maxWidth: 360, minWidth: 0, fontWeight: 700 }} value={flujo.nombre || ''} onChange={e => cambiar({ ...flujo, nombre: e.target.value })} aria-label="Nombre del flujo" />
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-2)' }}>
          Voz
          <select style={{ ...inp, width: 130, padding: '7px 10px' }} value={flujo.voz} onChange={e => cambiar({ ...flujo, voz: e.target.value })}>
            {estado.voces.map(v => <option key={v} value={v}>{NOMBRE_VOZ(v)}</option>)}
          </select>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-2)', cursor: 'pointer' }} title="Si contesta un buzón de voz, cuelga sin reproducir el flujo y no se cobra">
          <input type="checkbox" checked={!!flujo.detectar_buzon} onChange={e => cambiar({ ...flujo, detectar_buzon: e.target.checked })} />
          Detectar buzón
        </label>
        <div style={{ flex: 1 }} />
        {errores.length > 0 && !cambios && <Pendientes errores={errores} />}
        <button style={btnSec} disabled={cambios || errores.length > 0} title={cambios ? 'Guarda primero' : errores.length ? 'Resuelve los pendientes' : ''}
          onClick={() => setPrueba({ telefono: '' })}><PhoneCall size={14} />Probar en mi celular</button>
        <button style={{ ...btn, opacity: cambios ? 1 : 0.6 }} disabled={guardando} onClick={guardar}><Save size={14} />{guardando ? 'Guardando...' : cambios ? 'Guardar' : 'Guardado'}</button>
      </div>
      {msg && <Aviso msg={msg} onCerrar={() => setMsg(null)} />}

      <div style={{ flex: 1, display: 'flex', gap: 12, minHeight: 0 }} className="r-col">
        <div className="r-lienzo" style={{ flex: 1, position: 'relative', borderRadius: 12, border: '1px solid var(--border)', overflow: 'hidden', background: 'var(--bg-2)', minHeight: 420 }}>
          <div style={{ position: 'absolute', zIndex: 5, top: 10, left: 10, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {Object.entries(TIPOS).map(([k, t]) => {
              const Icono = t.icono;
              return (
                <button key={k} onClick={() => agregar(k)} title={t.ayuda} style={{ ...btnSec, background: 'var(--surface)', color: 'var(--text)', padding: '6px 10px' }}>
                  <Icono size={13} color={t.color} />{t.nombre}
                </button>
              );
            })}
          </div>
          <ReactFlow nodes={rfNodes} edges={rfEdges} nodeTypes={tiposNodo} onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange} onConnect={onConnect} onPaneClick={() => setSel(null)}
            deleteKeyCode={['Backspace', 'Delete']} {...vistaInicial}
            colorMode={document.documentElement.getAttribute('data-theme') === 'light' ? 'light' : 'dark'}
            proOptions={{ hideAttribution: true }} minZoom={0.3}>
            <Background gap={20} size={1} color="var(--border-2)" />
            <Controls showInteractive={false} />
          </ReactFlow>
          <div className="r-oculto" style={{ position: 'absolute', bottom: 10, right: 12, left: 60, textAlign: 'right', fontSize: 11, color: 'var(--text-3)', zIndex: 5, pointerEvents: 'none' }}>
            Arrastra desde los puntos de la derecha hasta otro paso para conectarlos. Selecciona una línea y pulsa Supr para quitarla.
          </div>
        </div>
        <div className="r-solo-celular" style={{ fontSize: 11, color: 'var(--text-3)', lineHeight: 1.5 }}>
          Mueve el lienzo con un dedo y acerca con dos. Toca un paso para editarlo abajo; para conectar, arrastra desde el punto de la derecha de un paso hasta otro.
        </div>
        <div ref={panelRef} className="r-panel" style={{ width: 340, flexShrink: 0, overflowY: 'auto', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 16, scrollMarginTop: 12 }}>
          {nodoSel
            ? <PanelPaso key={sel} nid={sel} nodo={nodoSel} flujo={flujo} cambiarNodo={cambiarNodo}
                esInicio={flujo.inicio === sel} hacerInicio={() => cambiar({ ...flujo, inicio: sel })} quitar={() => quitar(sel)} />
            : <div style={{ fontSize: 13, color: 'var(--text-3)', lineHeight: 1.6 }}>
                <div style={{ fontWeight: 700, color: 'var(--text)', marginBottom: 6 }}>Selecciona un paso para editarlo</div>
                Agrega pasos con los botones de arriba. El paso marcado como <b>Inicio</b> es lo primero que escucha la persona al contestar.
                <div style={{ marginTop: 14 }}>Puedes usar datos de la base en los textos, por ejemplo <code>{'{primer_nombre}'}</code> o cualquier columna extra que subas, como <code>{'{Balance}'}</code>.</div>
              </div>}
        </div>
      </div>
      {prueba && <Prueba flujo={flujo} onCerrar={() => setPrueba(null)} />}
    </div>
  );
}

function Pendientes({ errores }) {
  const [abierto, setAbierto] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      <button style={{ ...btnSec, color: 'var(--warning)', borderColor: 'color-mix(in srgb, var(--warning) 40%, transparent)' }} onClick={() => setAbierto(a => !a)}>
        <AlertTriangle size={13} />{errores.length} pendiente{errores.length > 1 ? 's' : ''}
      </button>
      {abierto && (
        <div style={{ position: 'absolute', right: 0, top: 38, zIndex: 20, width: 320, maxWidth: 'calc(100vw - 32px)', background: 'var(--surface)', border: '1px solid var(--border-2)', borderRadius: 10, padding: 12, boxShadow: 'var(--shadow)' }}>
          {errores.map((e, i) => <div key={i} style={{ fontSize: 12, color: 'var(--text-2)', padding: '4px 0' }}>• {e}</div>)}
        </div>
      )}
    </div>
  );
}

// ── Panel lateral del paso seleccionado ─────────────────────────────────────
function PanelPaso({ nid, nodo, flujo, cambiarNodo, esInicio, hacerInicio, quitar }) {
  const t = TIPOS[nodo.tipo];
  const set = parche => cambiarNodo(nid, parche);
  const otros = Object.entries(flujo.nodos).filter(([id]) => id !== nid);
  const selectorDestino = (valor, alCambiar) => (
    <select style={inp} value={valor || ''} onChange={e => alCambiar(e.target.value || undefined)}>
      <option value="">— Colgar —</option>
      {otros.map(([id, n]) => <option key={id} value={id}>{n.titulo || TIPOS[n.tipo]?.nombre}</option>)}
    </select>
  );
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', color: t.color }}>{t.nombre}</span>
        <span style={{ fontSize: 11, color: 'var(--text-3)' }}>{t.ayuda}</span>
      </div>
      <div><label style={etiqueta}>Nombre del paso</label>
        <input style={inp} value={nodo.titulo || ''} onChange={e => set({ titulo: e.target.value })} /></div>

      {['mensaje', 'pregunta', 'colgar'].includes(nodo.tipo) && <AudioPaso nodo={nodo} flujo={flujo} set={set} />}

      {nodo.tipo === 'pregunta' && <Opciones nodo={nodo} set={set} selectorDestino={selectorDestino} />}

      {nodo.tipo === 'resultado' && <>
        <div><label style={etiqueta}>Estado del contacto</label>
          <select style={inp} value={nodo.status} onChange={e => set({ status: e.target.value })}>
            <option value="Finalizada">Finalizada (contactado)</option>
            <option value="Rechazada">Rechazada</option>
            <option value="No contesta">No contesta (entra en el próximo barrido)</option>
          </select></div>
        <div><label style={etiqueta}>Resultado (así aparece en Llamadas e informes)</label>
          <input style={inp} value={nodo.resultado || ''} placeholder="Ej: Compromiso de pago" onChange={e => set({ resultado: e.target.value })} /></div>
      </>}

      {nodo.tipo === 'transferir' && <div><label style={etiqueta}>Número del asesor</label>
        <input style={inp} value={nodo.numero || ''} placeholder="Ej: 3001234567" inputMode="tel" onChange={e => set({ numero: e.target.value })} />
        <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 6, lineHeight: 1.5 }}>Sale por la misma troncal de la campaña. Si el asesor no contesta, la persona escucha un aviso y la llamada termina.</div></div>}

      {['mensaje', 'resultado'].includes(nodo.tipo) && <div><label style={etiqueta}>Después va a</label>{selectorDestino(nodo.siguiente, v => set({ siguiente: v }))}</div>}

      <div style={{ display: 'flex', gap: 8, borderTop: '1px solid var(--border)', paddingTop: 14, flexWrap: 'wrap' }}>
        {!esInicio && <button style={btnSec} onClick={hacerInicio}><Flag size={13} />Empezar aquí</button>}
        <button style={{ ...btnSec, color: 'var(--danger)' }} onClick={quitar}><X size={13} />Quitar paso</button>
      </div>
    </div>
  );
}

function AudioPaso({ nodo, flujo, set }) {
  const a = nodo.audio || { modo: 'texto', texto: '' };
  const [sonando, setSonando] = useState(null);
  const [subiendo, setSubiendo] = useState(false);
  const [err, setErr] = useState(null);
  const ref = useRef(null);
  const texto = useRef(null);
  useEffect(() => () => sonando && sonando.pause(), [sonando]);

  const parar = () => { sonando && sonando.pause(); setSonando(null); };
  const escuchar = async () => {
    if (sonando) return parar();
    setErr(null);
    try {
      const p = a.modo === 'grabacion'
        ? api.get(`/flujos-voz/audio/${a.archivo}`, { responseType: 'blob' })
        : api.post('/flujos-voz/escuchar', { texto: a.texto, voz: flujo.voz }, { responseType: 'blob' });
      setSonando(await reproducir(p, () => setSonando(null)));
    } catch (e) { setErr('No se pudo reproducir'); }
  };
  const subir = async ev => {
    const f = ev.target.files?.[0];
    if (!f) return;
    setSubiendo(true); setErr(null);
    const fd = new FormData(); fd.append('archivo', f);
    try {
      const r = await api.post('/flujos-voz/audio', fd);
      set({ audio: { modo: 'grabacion', archivo: r.data.id, nombre: `${r.data.nombre} (${r.data.segundos} s)` } });
    } catch (e) { setErr(error(e, 'No se pudo subir el audio')); }
    setSubiendo(false); ev.target.value = '';
  };
  const insertar = v => {
    const el = texto.current; const tok = `{${v}}`; const val = a.texto || '';
    const i = el ? el.selectionStart : val.length;
    set({ audio: { ...a, texto: val.slice(0, i) + tok + val.slice(i) } });
  };
  const pestana = act => ({ flex: 1, padding: '7px 0', fontSize: 12, fontWeight: 700, cursor: 'pointer', border: 'none', borderRadius: 7,
    background: act ? 'var(--surface-2)' : 'transparent', color: act ? 'var(--text)' : 'var(--text-3)' });
  return (
    <div>
      <label style={etiqueta}>Qué escucha la persona</label>
      <div style={{ display: 'flex', gap: 4, padding: 3, background: 'var(--bg)', borderRadius: 9, marginBottom: 8 }}>
        <button style={pestana(a.modo !== 'grabacion')} onClick={() => set({ audio: { modo: 'texto', texto: a.texto || '' } })}>Voz generada</button>
        <button style={pestana(a.modo === 'grabacion')} onClick={() => set({ audio: { ...a, modo: 'grabacion' } })}>Grabación</button>
      </div>
      {a.modo === 'grabacion'
        ? <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ fontSize: 12, color: a.archivo ? 'var(--text)' : 'var(--text-3)' }}>{a.archivo ? a.nombre || 'Grabación' : 'Sube un MP3 o WAV de hasta 3 minutos.'}</div>
            <input ref={ref} type="file" accept="audio/*" style={{ display: 'none' }} onChange={subir} />
            <button style={btnSec} onClick={() => ref.current?.click()} disabled={subiendo}><Upload size={13} />{subiendo ? 'Subiendo...' : a.archivo ? 'Cambiar grabación' : 'Subir grabación'}</button>
          </div>
        : <>
            <textarea ref={texto} style={{ ...inp, minHeight: 110, resize: 'vertical', lineHeight: 1.5 }} value={a.texto || ''}
              placeholder="Hola {primer_nombre}, te llamamos de..." onChange={e => set({ audio: { modo: 'texto', texto: e.target.value } })} />
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 6 }}>
              {VARIABLES.map(v => <button key={v} onClick={() => insertar(v)} style={{ ...btnSec, padding: '3px 8px', fontSize: 11 }}>{`{${v}}`}</button>)}
            </div>
          </>}
      {(a.modo === 'grabacion' ? a.archivo : (a.texto || '').trim()) &&
        <button style={{ ...btnSec, marginTop: 8 }} onClick={escuchar}>{sonando ? <><Square size={12} />Detener</> : <><Play size={12} />Escuchar</>}</button>}
      {err && <div style={{ fontSize: 12, color: 'var(--danger)', marginTop: 6 }}>{err}</div>}
    </div>
  );
}

function Opciones({ nodo, set, selectorDestino }) {
  const ops = nodo.opciones || [];
  const setOp = (i, parche) => set({ opciones: ops.map((o, j) => j === i ? { ...o, ...parche } : o) });
  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
        <div><label style={etiqueta}>Responde con</label>
          <select style={{ ...inp, padding: '8px 6px' }} value={nodo.escucha} onChange={e => set({ escucha: e.target.value })}>
            <option value="voz_tecla">Voz o tecla</option><option value="voz">Solo voz</option><option value="tecla">Solo tecla</option>
          </select></div>
        <div><label style={etiqueta}>Espera (s)</label>
          <input style={inp} type="number" min={2} max={15} value={nodo.espera ?? 6} onChange={e => set({ espera: Number(e.target.value) })} /></div>
        <div><label style={etiqueta}>Repetir</label>
          <select style={{ ...inp, padding: '8px 6px' }} value={nodo.reintentos ?? 1} onChange={e => set({ reintentos: Number(e.target.value) })}>
            {[0, 1, 2, 3].map(n => <option key={n} value={n}>{n === 0 ? 'No' : `${n} vez${n > 1 ? 'es' : ''}`}</option>)}
          </select></div>
      </div>
      <div>
        <label style={etiqueta}>Opciones</label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {ops.map((o, i) => (
            <div key={o.id} style={{ border: '1px solid var(--border-2)', borderRadius: 10, padding: 10, display: 'flex', flexDirection: 'column', gap: 7 }}>
              <div style={{ display: 'flex', gap: 6 }}>
                <input style={{ ...inp, fontWeight: 700 }} value={o.nombre} placeholder="Nombre" onChange={e => setOp(i, { nombre: e.target.value })} />
                <input style={{ ...inp, width: 64, textAlign: 'center' }} value={o.tecla || ''} maxLength={1} placeholder="Tecla" aria-label="Tecla"
                  onChange={e => setOp(i, { tecla: e.target.value.replace(/[^0-9*#]/g, '') })} />
                <button style={{ ...btnSec, padding: '0 8px' }} aria-label="Quitar opción" onClick={() => set({ opciones: ops.filter((_, j) => j !== i) })}><X size={13} /></button>
              </div>
              <input style={inp} value={(o.palabras || []).join(', ')} placeholder="Palabras que cuentan, separadas por coma"
                onChange={e => setOp(i, { palabras: e.target.value.split(',').map(s => s.trimStart()) })}
                onBlur={e => setOp(i, { palabras: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: 'var(--text-3)', whiteSpace: 'nowrap' }}>Va a {selectorDestino(o.siguiente, v => setOp(i, { siguiente: v }))}</div>
            </div>
          ))}
          {ops.length < 9 && <button style={btnSec} onClick={() => set({ opciones: [...ops, { id: `o${Math.random().toString(36).slice(2, 7)}`, nombre: '', palabras: [], tecla: String(ops.length + 1) }] })}><Plus size={13} />Agregar opción</button>}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 8, lineHeight: 1.5 }}>Si dice varias, gana la frase más larga: “no puedo” le gana a “puedo”.</div>
      </div>
      <div><label style={etiqueta}>Si no responde</label>{selectorDestino(nodo.sin_respuesta, v => set({ sin_respuesta: v }))}</div>
      <div><label style={etiqueta}>Si responde algo que no es una opción</label>{selectorDestino(nodo.no_entendio, v => set({ no_entendio: v }))}</div>
    </>
  );
}

// ── Llamada de prueba ───────────────────────────────────────────────────────
function Prueba({ flujo, onCerrar }) {
  const [tel, setTel] = useState('');
  const [id, setId] = useState(null);
  const [res, setRes] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    if (!id) return;
    const t = setInterval(async () => {
      try {
        const r = await api.get(`/flujos-voz/prueba/${id}`);
        setRes(r.data);
        if (r.data.status) clearInterval(t);
      } catch (e) { /* sigue intentando */ }
    }, 2000);
    return () => clearInterval(t);
  }, [id]);
  const llamar = async () => {
    setErr(null);
    try { const r = await api.post(`/flujos-voz/${flujo.id}/probar`, { telefono: tel }); setId(r.data.id); setRes({}); }
    catch (e) { setErr(error(e, 'No se pudo llamar')); }
  };
  return (
    <div role="dialog" aria-modal="true" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 100, display: 'grid', placeItems: 'center', padding: 16 }}>
      <div style={{ width: 'min(440px, 100%)', background: 'var(--surface)', border: '1px solid var(--border-2)', borderRadius: 14, padding: 22, boxShadow: 'var(--shadow)' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', flex: 1 }}>Probar «{flujo.nombre}»</div>
          <button onClick={onCerrar} aria-label="Cerrar" style={{ background: 'none', border: 'none', color: 'var(--text-3)', cursor: 'pointer' }}><X size={16} /></button>
        </div>
        {!id ? <>
          <p style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 0, lineHeight: 1.55 }}>Te llamamos por la troncal de tu cuenta. Las pruebas no se cobran ni cambian tus campañas. Los datos como {'{nombre}'} se llenan con “Prueba”.</p>
          <input style={inp} value={tel} inputMode="tel" placeholder="Tu celular, ej: 3001234567" onChange={e => setTel(e.target.value)} onKeyDown={e => e.key === 'Enter' && llamar()} />
          {err && <div style={{ fontSize: 12, color: 'var(--danger)', marginTop: 8 }}>{err}</div>}
          <button style={{ ...btn, marginTop: 14 }} onClick={llamar}><PhoneCall size={14} />Llamarme</button>
        </> : <>
          <div style={{ fontSize: 13, fontWeight: 700, color: res?.status ? 'var(--success)' : 'var(--text-2)', marginBottom: 10 }}>
            {res?.status ? `${res.status} · ${res.resultado} (${res.segundos} s)` : res?.contestada ? 'En curso...' : 'Marcando...'}
          </div>
          <pre style={{ whiteSpace: 'pre-wrap', fontSize: 12, color: 'var(--text-2)', background: 'var(--bg)', borderRadius: 9, padding: 12, maxHeight: 260, overflowY: 'auto', margin: 0, fontFamily: 'inherit', lineHeight: 1.55 }}>
            {res?.transcripcion || 'Aquí aparece lo que dice el flujo y lo que respondes.'}
          </pre>
        </>}
      </div>
    </div>
  );
}
